import { FaApple, FaGooglePlay } from 'react-icons/fa';
import { IOS_APP_STORE_URL, PLAY_STORE_URL } from '../../../config/app-store';
import { trackUserEvent } from '../../../utils/reddit-pixel';

interface AppStoreBadgesProps {
  className?: string;
  size?: 'sm' | 'md' | 'lg';
}

// App-store clicks are our app-download conversion. We dual-fire:
//   - Lead: the standard event Reddit can optimize a campaign toward
//     (Reddit only exposes Lead/SignUp as conversion goals; custom
//     events are not selectable for optimization).
//   - Custom DownloadApp_<store>: not optimizable, but visible in Reddit
//     Events Manager for the iOS-vs-Android split and audience building.
// Both go through trackUserEvent (consent-gated). These badges live on
// the public landing page, so cold ad traffic is anonymous and the gate
// passes; this is intentionally no privacy-behavior change.
// 'TestFlight' was the pre-launch value for the same badge slot — keep that in
// mind when reading historical DownloadApp_* splits in Reddit Events Manager.
function trackAppDownload(store: 'AppStore' | 'GooglePlay') {
  trackUserEvent('Lead');
  trackUserEvent('Custom', { customEventName: `DownloadApp_${store}` });
}

export default function AppStoreBadges({ className = '', size = 'md' }: AppStoreBadgesProps) {
  const sizeClasses = {
    sm: 'h-10 px-4 text-sm',
    md: 'h-12 px-5 text-base',
    lg: 'h-14 px-6 text-lg',
  };

  const iconSizes = {
    sm: 'text-lg',
    md: 'text-xl',
    lg: 'text-2xl',
  };

  const baseClasses = `flex items-center gap-2 bg-(--ee-store-badge) text-(--ee-on-fill) rounded-lg`;
  const activeClasses = `hover:bg-(--ee-store-badge-hover) transition-colors`;

  return (
    <div className={`flex flex-row flex-wrap gap-3 ${className}`}>
      <a
        href={IOS_APP_STORE_URL}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => trackAppDownload('AppStore')}
        className={`${baseClasses} ${sizeClasses[size]} ${activeClasses}`}
        aria-label="Download EmpathEase on the App Store"
      >
        <FaApple className={iconSizes[size]} />
        <div className="flex flex-col items-start leading-tight">
          <span className="text-[10px] opacity-80">Download on the</span>
          <span className="font-semibold">App Store</span>
        </div>
      </a>

      <a
        href={PLAY_STORE_URL}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => trackAppDownload('GooglePlay')}
        className={`${baseClasses} ${sizeClasses[size]} ${activeClasses}`}
        aria-label="Get EmpathEase on Google Play"
      >
        <FaGooglePlay className={iconSizes[size]} />
        <div className="flex flex-col items-start leading-tight">
          <span className="text-[10px] opacity-80">Get it on</span>
          <span className="font-semibold">Google Play</span>
        </div>
      </a>
    </div>
  );
}
